<script setup lang="ts">
import { uploadImage, fetchImage } from "@/api";
import {
  DArrowLeft,
  DArrowRight,
  UploadFilled,
  FullScreen,
} from "@element-plus/icons-vue";
import TiptapUnderline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import ImageResize from "tiptap-extension-resize-image";
import type { UploadFile, UploadRawFile } from "element-plus";
import { useElementSize } from "@vueuse/core";

const { t } = useI18n();

const props = defineProps<{
  modelValue: string;
  allowedActions?: string[];
  allowFullscreen?: boolean;
}>();

const allowedActions = computed(
  () =>
    props.allowedActions || [
      "paragraph",
      "heading-3",
      "bold",
      "italic",
      "underline",
    ]
);
const emit = defineEmits(["update:modelValue", "update:text"]);

const editor = useEditor({
  content: props.modelValue,
  extensions: [
    TiptapStarterKit,
    TiptapUnderline,
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
    }),
    ImageResize.configure({
      inline: false,
      allowBase64: true,
    }),
  ],
  onUpdate: () => {
    emit("update:text", editor.value?.getText());
    emit("update:modelValue", editor.value?.getHTML());
  },
});
watch(
  () => props.modelValue,
  (value) => {
    if (value !== editor.value?.getHTML()) {
      editor.value?.commands.setContent(value);
    }
  }
);
const setLink = () => {
  ElMessageBox.prompt(
    t("c-widget-TipTap-client-please-input-link"),
    t("c-widget-TipTap-client-tip"),
    {
      confirmButtonText: t("c-widget-TipTap-client-ok"),
      cancelButtonText: t("c-widget-TipTap-client-cancel"),
    }
  )
    .then(({ value }) => {
      if (value === null) {
        return;
      }
      if (value === "") {
        editor.value?.chain().focus().extendMarkRange("link").unsetLink().run();
        return;
      }
      editor.value
        ?.chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: value })
        .run();
    })
    .catch(() => {
      ElMessage({
        type: "info",
        message: t("c-widget-TipTap-client-input-canceled"),
      });
    });
};
const loading = ref(false);
const showImageUpload = ref(false);
const fileList = ref<UploadFile[]>([]);

const handleChange = (file: UploadFile) => {
  fileList.value[0] = file;
};
const validate = (file: UploadRawFile) => {
  const isJPG = file.type === "image/jpeg" || file.type === "image/jpg";
  const isPNG = file.type === "image/png";
  const isLt2M = file.size / 1024 / 1024 < 2;

  if (!isJPG && !isPNG) {
    ElMessage.error(t("c-widget-TipTap-client-picture-format"));
    return false;
  }

  if (!isLt2M) {
    setTimeout(() => {
      ElMessage.error(t("c-widget-TipTap-client-picture-size"));
    }, 10);
    return false;
  }

  return true;
};
const applyFile = async () => {
  if (!validate(fileList.value[0].raw as UploadRawFile)) {
    return;
  }
  loading.value = true;
  const base64: string = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(fileList.value[0].raw as UploadRawFile);
  });
  if (base64) {
    const pic = await uploadImage(base64);
    editor.value
      ?.chain()
      .focus()
      .setImage({ src: fetchImage(pic.picture) })
      .run();
  }
  showImageUpload.value = false;
  loading.value = false;
};
const fullscreen = ref(false);
const headerRef = ref<HTMLElement | null>(null);
const { height: headerHeight } = useElementSize(headerRef);
const scrollbarStyles = computed(() => {
  return fullscreen.value
    ? `height: calc(100vh - ${headerHeight.value + 60}px)`
    : `max-height: calc(100vh - ${headerHeight.value + 120}px)`;
});
onBeforeUnmount(() => {
  editor.value?.destroy();
});
</script>

<template>
  <div class="editor-wrapper" :class="{ fullscreen: fullscreen }">
    <el-tooltip
      v-if="allowFullscreen"
      class="box-item"
      effect="dark"
      :content="t('c-widget-TipTap-client-toggle-fullscreen')"
      placement="top-start"
    >
      <el-button
        type="primary"
        plain
        circle
        v-if="allowFullscreen"
        @click="fullscreen = !fullscreen"
        class="fullscreen-toggle"
        :icon="FullScreen"
      >
      </el-button>
    </el-tooltip>
    <el-card v-if="editor" class="editor-card">
      <el-dialog
        :title="t('c-widget-TipTap-client-upload-image')"
        v-model="showImageUpload"
      >
        <el-upload
          v-loading="loading"
          class="upload-preview mr-4"
          :show-file-list="false"
          :auto-upload="true"
          :file-list="fileList"
          :http-request="applyFile"
          :on-change="handleChange"
          drag
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            {{ t("c-widget-TipTap-client-drop-file-here") }}
            <em>{{ t("c-widget-TipTap-client-click-to-upload") }}</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              {{ t("c-widget-TipTap-client-jpg-png-files") }}
            </div>
          </template>
        </el-upload>
      </el-dialog>
      <div ref="headerRef">
        <div>
          <el-dropdown trigger="click">
            <el-button
              v-if="allowedActions.includes('paragraph')"
              class="mr-2 heading-button"
              :class="{ 'is-active': !editor.isActive('paragraph') }"
            >
              <span v-if="editor.isActive('paragraph')">p</span>
              <span v-else-if="editor.isActive('heading', { level: 1 })"
                >H1</span
              >
              <span v-else-if="editor.isActive('heading', { level: 2 })"
                >H2</span
              >
              <span v-else-if="editor.isActive('heading', { level: 3 })"
                >H3</span
              >
              <span v-else>-\-</span>
            </el-button>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-if="allowedActions.includes('heading-1')"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  "
                >
                  {{ t("c-widget-TipTap-client-heading-1") }}
                </el-dropdown-item>
                <el-dropdown-item
                  v-if="allowedActions.includes('heading-2')"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  "
                >
                  {{ t("c-widget-TipTap-client-heading-2") }}
                </el-dropdown-item>
                <el-dropdown-item
                  v-if="allowedActions.includes('heading-3')"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  "
                >
                  {{ t("c-widget-TipTap-client-heading-3") }}
                </el-dropdown-item>
                <el-dropdown-item
                  @click="editor.chain().focus().setParagraph().run()"
                >
                  {{ t("c-widget-TipTap-client-paragraph") }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
          <el-button
            v-if="allowedActions.includes('bold')"
            @click="editor.chain().focus().toggleBold().run()"
            :disabled="!editor.can().chain().focus().toggleBold().run()"
            :class="{ 'is-active': editor.isActive('bold') }"
          >
            <b>b</b>
          </el-button>
          <el-button
            v-if="allowedActions.includes('italic')"
            @click="editor.chain().focus().toggleItalic().run()"
            :disabled="!editor.can().chain().focus().toggleItalic().run()"
            :class="{ 'is-active': editor.isActive('italic') }"
          >
            <i>i</i>
          </el-button>
          <el-button
            v-if="allowedActions.includes('underline')"
            @click="editor.chain().focus().toggleUnderline().run()"
            :disabled="!editor.can().chain().focus().toggleUnderline().run()"
            :class="{ 'is-active': editor.isActive('underline') }"
          >
            <span class="decoration-underline">u</span>
          </el-button>
          <el-button
            v-if="allowedActions.includes('strike')"
            @click="editor.chain().focus().toggleStrike().run()"
            :disabled="!editor.can().chain().focus().toggleStrike().run()"
            :class="{ 'is-active': editor.isActive('strike') }"
          >
            <span class="decoration-line-through">stroke</span>
          </el-button>
          <el-button
            v-if="allowedActions.includes('link')"
            @click="
              editor.isActive('link')
                ? editor.chain().focus().unsetLink().run()
                : setLink()
            "
            :class="{ 'is-active': editor.isActive('link') }"
          >
            <span>{{ decodeURI("%3C/a%3E") }}</span>
          </el-button>
          <el-button
            v-if="allowedActions.includes('bulletList')"
            @click="editor.chain().focus().toggleBulletList().run()"
            :class="{ 'is-active': editor.isActive('bulletList') }"
          >
            <span class="mini-list">
              <span>• -</span>
              <span>• -</span>
            </span>
          </el-button>
          <el-button
            v-if="allowedActions.includes('orderedList')"
            @click="editor.chain().focus().toggleOrderedList().run()"
            :class="{ 'is-active': editor.isActive('orderedList') }"
          >
            <span class="mini-list">
              <span> 1 -</span>
              <span> 2 -</span>
            </span>
          </el-button>

          <el-button
            v-if="allowedActions.includes('code')"
            @click="editor.chain().focus().toggleCode().run()"
            :disabled="!editor.can().chain().focus().toggleCode().run()"
            :class="{ 'is-active': editor.isActive('code') }"
          >
            code
          </el-button>
          <el-button
            v-if="allowedActions.includes('codeBlock')"
            @click="editor.chain().focus().toggleCodeBlock().run()"
            :class="{ 'is-active': editor.isActive('codeBlock') }"
          >
            code block
          </el-button>
          <el-button
            v-if="allowedActions.includes('blockquote')"
            @click="editor.chain().focus().toggleBlockquote().run()"
            :class="{ 'is-active': editor.isActive('blockquote') }"
          >
            blockquote
          </el-button>
          <el-button
            @click="showImageUpload = true"
            v-if="allowedActions.includes('image')"
          >
            {{ t("c-widget-TipTap-client-image") }}
          </el-button>
          <el-button
            v-if="allowedActions.includes('divider')"
            @click="editor.chain().focus().setHorizontalRule().run()"
          >
            {{ t("c-widget-TipTap-client-divider") }}
          </el-button>
          <el-button
            v-if="allowedActions.includes('break')"
            @click="editor.chain().focus().setHardBreak().run()"
          >
            {{ t("c-widget-TipTap-client-break") }}
          </el-button>
          <el-button
            v-if="allowedActions.includes('clearMarks')"
            @click="editor.chain().focus().unsetAllMarks().run()"
          >
            {{ t("c-widget-TipTap-client-clear-marks") }}
          </el-button>
        </div>
        <el-divider></el-divider>
      </div>
      <el-scrollbar :wrap-style="scrollbarStyles" :data-st="scrollbarStyles">
        <TiptapEditorContent
          class="tiptap-editor"
          :editor="editor"
          @update="emit('update:modelValue', $event.target.value)"
        />
      </el-scrollbar>
      <div class="flex mt-4">
        <div class="flex-grow"></div>
        <el-button
          v-if="allowedActions.includes('undo')"
          @click="editor.chain().focus().undo().run()"
          :disabled="!editor.can().chain().focus().undo().run()"
        >
          <el-icon><DArrowLeft /></el-icon>
        </el-button>
        <el-button
          v-if="allowedActions.includes('redo')"
          @click="editor.chain().focus().redo().run()"
          :disabled="!editor.can().chain().focus().redo().run()"
        >
          <el-icon><DArrowRight /></el-icon>
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<style lang="scss">
.is-error .tiptap-editor > .ProseMirror {
  border-color: var(--el-color-error) !important;
}
</style>
<style lang="scss" scoped>
.editor-wrapper {
  position: relative;
  .fullscreen-toggle {
    position: absolute;
    top: 5px;
    right: 0;
    z-index: 10;
  }
  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    background: var(--el-bg-color-page);
  }
}
* {
  outline: none;
}
.tiptap-editor {
  background: var(--el-bg-color-page);
  :deep(> .ProseMirror) {
    padding: 1rem;
    border: 1px solid var(--el-border-color-light);
    border-radius: var(--el-card-border-radius);
  }
  :deep(> .ProseMirror-focused) {
    border: 1px solid var(--el-color-primary);
    outline: none;
  }
  :deep(.ProseMirror-selectednode) {
    box-shadow: 0 0 5px 5px var(--el-color-primary);
  }
}
.mini-list {
  display: block;
  font-size: 0.5rem;
  * {
    display: block;
  }
}
.el-button + .el-button {
  margin-left: 0;
}
.el-button {
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
}

.heading-button {
  min-width: 60px;
}
</style>
